import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Volume2, Eye, Flag, Play, Pause } from 'lucide-react';
import { supabase, getLessonsTable, getUnknownWordsTable } from '../utils/supabase';
import { WordTestPage } from './WordTestPage';
import { playSuccessSound, playLessonCompleteSound } from '../utils/audio';
import { splitIntoSentences } from '../utils/textProcessing';
import { useTranslation } from '../utils/translations';
import { ReportIssueModal } from './ReportIssueModal';
import { UnderstandingSurvey } from './UnderstandingSurvey';
import { HighUnderstandingOptions } from './HighUnderstandingOptions';
import { type LanguageLevel } from '../types';

export function LessonViewer() {
  const [loading, setLoading] = useState(true);
  const [showUnknownWordsQuiz, setShowUnknownWordsQuiz] = useState(true);
  const [nativeLanguage, setNativeLanguage] = useState<string>('English');
  const [showReportModal, setShowReportModal] = useState(false);
  const [lessonTitle, setLessonTitle] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [isMobile] = useState(() => 
    typeof window !== 'undefined' && 
    ('ontouchstart' in window || navigator.maxTouchPoints > 0 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent))
  );
  const [unknownWords, setUnknownWords] = useState<string[]>([]);
  const [canSkip, setCanSkip] = useState(false);
  const [showText, setShowText] = useState<boolean>(true);
  const [shouldShowQuiz, setShouldShowQuiz] = useState(true);
  const [lessonTranslation, setLessonTranslation] = useState<string>('');
  const [isInitialLoad, setIsInitialLoad] = useState(false);
  const [lessonText, setLessonText] = useState('');
  const [autoplayFailed, setAutoplayFailed] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [hasListenedToAudio, setHasListenedToAudio] = useState(false);
  const [autoplayAttempted, setAutoplayAttempted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showInitialSurvey, setShowInitialSurvey] = useState(false);
  const [initialUnderstanding, setInitialUnderstanding] = useState<number | null>(null);
  const [surveyId, setSurveyId] = useState<string | null>(null);
  const [showHighUnderstandingOptions, setShowHighUnderstandingOptions] = useState(false);
  const [currentLevel, setCurrentLevel] = useState<LanguageLevel>('A1');
  const [audioDuration, setAudioDuration] = useState<number>(0);
  const [audioCurrentTime, setAudioCurrentTime] = useState<number>(0);
  const [audioSegments, setAudioSegments] = useState<Array<{ text: string; translation?: string; audio_url?: string }>>([]);
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const playingPromiseRef = useRef<Promise<void> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const nextAudioRef = useRef<HTMLAudioElement | null>(null);
  const mountedRef = useRef(true);
  const autoplayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoplayAttemptedRef = useRef(false);
  const isPlayingSegmentRef = useRef(false);
  const segmentDurationsRef = useRef<number[]>([]);
  const totalDurationRef = useRef<number>(0);
  const elapsedTimeOffsetRef = useRef<number>(0);
  const navigate = useNavigate();
  const { lessonId } = useParams();

  // Autoplay audio when component mounts and audio is available
  useEffect(() => {
    const hasAudio = audioUrl || (audioSegments.length > 0 && audioSegments.some(s => s.audio_url));

    if (hasAudio && !hasListenedToAudio && !autoplayAttemptedRef.current && !isPlayingSegmentRef.current) {
      console.log('Autoplay conditions met. audioUrl:', audioUrl, 'segments:', audioSegments.length);
      autoplayAttemptedRef.current = true;
      setAutoplayAttempted(true);

      const autoplayDelay = 0;

      if (autoplayTimeoutRef.current) {
        clearTimeout(autoplayTimeoutRef.current);
      }

      autoplayTimeoutRef.current = setTimeout(() => {
        autoplayTimeoutRef.current = null;
        if (!mountedRef.current) return;
        if (isPlayingSegmentRef.current) return;
        try {
          console.log('Attempting autoplay on initial page load');
          playAudio();
        } catch (err) {
          console.error('Autoplay attempt failed:', err);
          setAutoplayFailed(true);
        }
      }, autoplayDelay);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl, audioSegments, hasListenedToAudio]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (autoplayTimeoutRef.current) {
        clearTimeout(autoplayTimeoutRef.current);
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      if (nextAudioRef.current) {
        nextAudioRef.current.src = '';
        nextAudioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    const loadUserData = async () => {
      try {
        // Reset component state when lesson changes
        setIsInitialLoad(true);
        setShowInitialSurvey(false);
        setShowHighUnderstandingOptions(false);
        setInitialUnderstanding(null);
        setSurveyId(null);
        setHasListenedToAudio(false);
        setAutoplayAttempted(false);
        autoplayAttemptedRef.current = false;
        isPlayingSegmentRef.current = false;
        setIsPlaying(false);
        setShowUnknownWordsQuiz(true);
        setAudioDuration(0);
        setAudioCurrentTime(0);
        segmentDurationsRef.current = [];
        totalDurationRef.current = 0;
        if (nextAudioRef.current) {
          nextAudioRef.current.src = '';
          nextAudioRef.current = null;
        }

        // Stop any playing audio and clear pending autoplay
        if (autoplayTimeoutRef.current) {
          clearTimeout(autoplayTimeoutRef.current);
          autoplayTimeoutRef.current = null;
        }
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }

        if (!lessonId) {
          console.error('No lesson ID provided');
          navigate('/lessons');
          return;
        }

        setLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUserId(session.user.id);

          const { data: profile } = await supabase
            .from('profiles')
            .select('is_admin, native_language, current_level')
            .eq('user_id', session.user.id)
            .maybeSingle();

          setIsAdmin(profile?.is_admin || false);
          const userNativeLanguage = profile?.native_language || 'English';
          setNativeLanguage(userNativeLanguage);
          setCurrentLevel(profile?.current_level || 'A1');

          const table = getLessonsTable(userNativeLanguage);
          console.log('Loading lesson from table:', table, 'with ID:', lessonId, 'for language:', userNativeLanguage);

          const [lessonResult, unknownWordsResult] = await Promise.all([
            supabase
              .from(table)
              .select(`
                id,
                level,
                title,
                description,
                text,
                text_translation,
                category,
                difficulty,
                is_review,
                show_text,
                audio_url,
                segments_timepoints,
                order_index,
                generated_for
              `)
              .eq('id', lessonId)
              .maybeSingle(),
            supabase
              .from(getUnknownWordsTable())
              .select('word')
              .eq('user_id', session.user.id)
              .eq('lesson_id', lessonId)
              .order('created_at', { ascending: false }),
          ]);

          const { data: lesson, error: lessonError } = lessonResult;
          const { data: unknownWordsData } = unknownWordsResult;

          if (lessonError) {
            console.error('Error loading lesson:', lessonError);
            throw lessonError;
          }

          if (!lesson) {
            console.error('No lesson found with id:', lessonId);
            throw new Error('Lesson not found');
          }

          console.log('Loaded lesson data:', lesson);

          if (!mountedRef.current) return;

          setLessonTitle(lesson.title || '');
          setShowText(lesson.show_text !== false);
          setLessonText(lesson.text || '');
          setLessonTranslation(lesson.text_translation || '');
          setAudioUrl(lesson.audio_url || null);
          setShowUnknownWordsQuiz(false);

          if (lesson.segments_timepoints && Array.isArray(lesson.segments_timepoints)) {
            const parsedSegments = lesson.segments_timepoints.map((seg: string | object) => {
              const segment = typeof seg === 'string' ? JSON.parse(seg) : seg;
              return {
                text: segment.text,
                translation: segment.translation,
                audio_url: segment.audio_url
              };
            });
            console.log('Parsed segments:', parsedSegments.length, 'segments with audio URLs:', parsedSegments.filter(s => s.audio_url).length);
            setAudioSegments(parsedSegments);
          }

          if (unknownWordsData) {
            const uniqueWords = [...new Set(unknownWordsData.map(w => w.word))];
            const randomWords = uniqueWords
              .sort(() => Math.random() - 0.5)
              .slice(0, 10);
            setUnknownWords(randomWords);
            setShowUnknownWordsQuiz(randomWords.length > 0);
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          console.error('Error loading lesson:', error.message);
        } else {
          console.error('Error loading lesson:', error);
        }
        navigate('/lessons');
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          setIsInitialLoad(false);
        }
      }
    };

    loadUserData();
  }, [lessonId, navigate]);

  useEffect(() => {
    if (audioSegments.length === 0) return;
    segmentDurationsRef.current = new Array(audioSegments.length).fill(0);
    totalDurationRef.current = 0;
    if (isMobile) {
      return;
    }
    const segmentsWithAudio = audioSegments.filter(s => s.audio_url);
    if (segmentsWithAudio.length === 0) return;

    segmentDurationsRef.current = new Array(audioSegments.length).fill(0);
    totalDurationRef.current = 0;

    let loadedCount = 0;
    const audioElements: HTMLAudioElement[] = [];

    audioSegments.forEach((seg, i) => {
      if (!seg.audio_url) {
        loadedCount++;
        return;
      }
      const a = new Audio();
      a.preload = 'metadata';
      audioElements.push(a);
      a.onloadedmetadata = () => {
        if (!mountedRef.current) return;
        segmentDurationsRef.current[i] = a.duration || 0;
        loadedCount++;
        const total = segmentDurationsRef.current.reduce((s, d) => s + (d || 0), 0);
        totalDurationRef.current = total;
        setAudioDuration(total);
        a.src = '';
      };
      a.onerror = () => {
        loadedCount++;
        a.src = '';
      };
      a.src = seg.audio_url;
    });

    return () => {
      audioElements.forEach(a => { a.src = ''; });
    };
  }, [audioSegments]);

  const { t } = useTranslation(nativeLanguage);

  const handleQuizComplete = (score: number) => {
    if (score >= 9) { // 9 or 10 correct answers
      stopRecording(); // Ensure any active recording is stopped
      setShowUnknownWordsQuiz(false); // Show lesson text instead of navigating
    } else {
      // Reload unknown words and retry quiz
      const loadNewWords = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: unknownWordsData } = await supabase
            .from('lesson_unknown_words')
            .select('word')
            .eq('user_id', user.id)
            .eq('lesson_id', lessonId)
            .order('created_at', { ascending: false });

          if (unknownWordsData) {
            // Get unique words and randomly select 10
            const uniqueWords = [...new Set(unknownWordsData.map(w => w.word))];
            const randomWords = uniqueWords
              .sort(() => Math.random() - 0.5) // Shuffle array
              .slice(0, 10);
            setUnknownWords(randomWords);
          }
        }
      };
      loadNewWords();
    }
  };

  const handleInitialSurveySubmit = async (percentage: number) => {
    try {
      const { data, error } = await supabase
        .from('lesson_understanding_surveys')
        .insert({
          user_id: userId,
          lesson_id: lessonId,
          initial_understanding: percentage
        })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        setSurveyId(data.id);
        setInitialUnderstanding(percentage);
      }
    } catch (error) {
      console.error('Error saving initial understanding:', error);
    }

    setShowInitialSurvey(false);

    // If user understood 100%, give them option to skip to next lesson
    if (percentage === 100) {
      // Play celebration sound
      playLessonCompleteSound();
      setShowHighUnderstandingOptions(true);
    } else {
      // Automatically navigate to practice page
      navigate(`/lesson/${lessonId}/practice`);
    }
  };

  const handleGoToNextLesson = async (nextLessonId: string) => {
    console.log('Navigating to next lesson:', nextLessonId);
    console.log('Current lesson:', lessonId);

    try {
      // Mark current lesson as completed
      const progressTable = nativeLanguage === 'Persian' ? 'persian_lesson_progress' : 'lesson_progress';

      const { data: existingProgress } = await supabase
        .from(progressTable)
        .select('id')
        .eq('user_id', userId)
        .eq('lesson_id', lessonId)
        .maybeSingle();

      if (existingProgress) {
        await supabase
          .from(progressTable)
          .update({ completed: true, updated_at: new Date().toISOString() })
          .eq('id', existingProgress.id);
        console.log('Updated existing progress');
      } else {
        await supabase
          .from(progressTable)
          .insert({
            user_id: userId,
            lesson_id: lessonId,
            completed: true
          });
        console.log('Created new progress entry');
      }
    } catch (error) {
      console.error('Error marking lesson as completed:', error);
    }

    setShowHighUnderstandingOptions(false);
    console.log('Navigating to:', `/lesson/${nextLessonId}`);
    navigate(`/lesson/${nextLessonId}`);
  };

  const handlePracticeAnyway = () => {
    setShowHighUnderstandingOptions(false);
    navigate(`/lesson/${lessonId}/practice`);
  };

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const hasPerSegmentAudio = audioSegments.length > 0 && audioSegments.some(s => s.audio_url);

  const preloadAudio = (url: string): HTMLAudioElement | null => {
    const audio = new Audio();
    audio.preload = 'auto';
    audio.src = url;
    return audio;
  };

  const playNextSegment = async (index: number, preloadedAudio?: HTMLAudioElement | null) => {
    console.log('playNextSegment called with index:', index, 'total segments:', audioSegments.length);

    if (index >= audioSegments.length) {
      if (mountedRef.current) {
        console.log('Finished playing all segments, showing survey');
        isPlayingSegmentRef.current = false;
        setIsPlaying(false);
        setHasListenedToAudio(true);
        setShowInitialSurvey(true);
        setCurrentSegmentIndex(0);
        const realTotal = elapsedTimeOffsetRef.current;
        if (realTotal > 0) {
          totalDurationRef.current = realTotal;
          setAudioDuration(realTotal);
          setAudioCurrentTime(realTotal);
        }
      }
      return;
    }

    if (!mountedRef.current) return;

    const segment = audioSegments[index];
    console.log('Playing segment', index, 'audio URL:', segment.audio_url);

    if (!segment.audio_url) {
      console.log('Segment has no audio URL, skipping to next');
      playNextSegment(index + 1);
      return;
    }

    setCurrentSegmentIndex(index);

    if (audioRef.current && audioRef.current !== preloadedAudio) {
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.ontimeupdate = null;
      audioRef.current.onloadedmetadata = null;
      try {
        audioRef.current.pause();
        audioRef.current.src = '';
      } catch {
        // Ignore pause errors
      }
    }

    if (!mountedRef.current) return;

    isPlayingSegmentRef.current = true;
    const audio = preloadedAudio || new Audio();
    audioRef.current = audio;

    audio.onloadedmetadata = () => {
      if (mountedRef.current) {
        segmentDurationsRef.current[index] = audio.duration;
        const knownTotal = segmentDurationsRef.current.reduce((sum, dur) => sum + (dur || 0), 0);
        const knownCount = segmentDurationsRef.current.filter(d => d > 0).length;
        const avgDuration = knownCount > 0 ? knownTotal / knownCount : 0;
        const unknownCount = audioSegments.length - knownCount;
        const estimatedTotal = knownTotal + avgDuration * unknownCount;
        const newTotal = Math.max(totalDurationRef.current, estimatedTotal);
        totalDurationRef.current = newTotal;
        setAudioDuration(newTotal);
      }
    };

    audio.ontimeupdate = () => {
      if (mountedRef.current && audioRef.current === audio) {
        setAudioCurrentTime(elapsedTimeOffsetRef.current + audio.currentTime);
      }
    };

    audio.onended = () => {
      console.log('Segment audio ended, playing next');
      if (mountedRef.current && audioRef.current === audio) {
        const segDur = audio.duration || 0;
        elapsedTimeOffsetRef.current += segDur;
        segmentDurationsRef.current[index] = segDur || segmentDurationsRef.current[index];

        const knownTotal = segmentDurationsRef.current.reduce((s, d) => s + (d || 0), 0);
        if (knownTotal > totalDurationRef.current) {
          totalDurationRef.current = knownTotal;
          setAudioDuration(knownTotal);
        }

        const preloaded = nextAudioRef.current;
        nextAudioRef.current = null;

        if (index + 2 < audioSegments.length) {
          const nextNext = audioSegments[index + 2];
          if (nextNext?.audio_url) {
            nextAudioRef.current = preloadAudio(nextNext.audio_url);
          }
        }

        playNextSegment(index + 1, preloaded || undefined);
      }
    };

    audio.onerror = (e) => {
      console.error('Error loading segment audio:', segment.audio_url, e);
      isPlayingSegmentRef.current = false;
      if (mountedRef.current) {
        playNextSegment(index + 1);
      }
    };

    if (nextAudioRef.current === null && index + 1 < audioSegments.length) {
      const nextSeg = audioSegments[index + 1];
      if (nextSeg?.audio_url) {
        nextAudioRef.current = preloadAudio(nextSeg.audio_url);
      }
    }

    if (!audio.src) {
      audio.src = segment.audio_url;
    }

    audio.play().then(() => {
      console.log('Successfully started playing segment', index);
    }).catch((error) => {
      console.error('Failed to play segment audio:', error);
      if (error?.name === 'NotAllowedError') {
        isPlayingSegmentRef.current = false;
        if (mountedRef.current) {
          setIsPlaying(false);
          setAutoplayFailed(true);
        }
      } else {
        if (mountedRef.current) {
          playNextSegment(index + 1);
        }
      }
    });
  };

  const playAudio = () => {
    console.log('playAudio called. isPlaying:', isPlaying, 'isPlayingSegmentRef:', isPlayingSegmentRef.current, 'hasPerSegmentAudio:', hasPerSegmentAudio, 'audioSegments.length:', audioSegments.length);

    if (autoplayTimeoutRef.current) {
      clearTimeout(autoplayTimeoutRef.current);
      autoplayTimeoutRef.current = null;
    }

    if (isPlaying || isPlayingSegmentRef.current) {
      if (audioRef.current) {
        console.log('Stopping active audio playback');
        audioRef.current.pause();
        audioRef.current.onended = null;
        audioRef.current.onerror = null;
        audioRef.current.currentTime = 0;
      }
      isPlayingSegmentRef.current = false;
      if (mountedRef.current) {
        setIsPlaying(false);
      }
      return;
    }

    if (hasPerSegmentAudio) {
      console.log('Playing per-segment audio with', audioSegments.length, 'segments');
      isPlayingSegmentRef.current = true;
      elapsedTimeOffsetRef.current = 0;
      setIsPlaying(true);
      setCurrentSegmentIndex(0);
      playNextSegment(0);
      return;
    }

    if (audioUrl) {
      console.log('Attempting to play audio:', audioUrl);

      if (!audioRef.current) {
        console.log('Creating new Audio element');
        audioRef.current = new Audio(audioUrl);

        audioRef.current.onloadedmetadata = () => {
          if (audioRef.current && mountedRef.current) {
            setAudioDuration(audioRef.current.duration);
          }
        };

        audioRef.current.ontimeupdate = () => {
          if (audioRef.current && mountedRef.current) {
            setAudioCurrentTime(audioRef.current.currentTime);
          }
        };

        audioRef.current.onended = () => {
          console.log('Audio playback ended naturally');
          playingPromiseRef.current = null;
          if (mountedRef.current) {
            setIsPlaying(false);
            setHasListenedToAudio(true);
            setShowInitialSurvey(true);
          }
        };

        audioRef.current.onerror = (error) => {
          if (!audioRef.current?.src) return;

          const mediaError = audioRef.current?.error;
          console.error('Audio playback error:', {
            code: mediaError?.code,
            message: mediaError?.message,
            event: error
          });
          playingPromiseRef.current = null;
          if (mountedRef.current) {
            setIsPlaying(false);
            setHasListenedToAudio(true);
          }
        };
      }

      if (playingPromiseRef.current) {
        console.log('Audio play operation already in progress, waiting...');
        return;
      }

      try {
        audioRef.current.pause();
      } catch (e) {
        console.warn('Error pausing audio:', e);
      }

      try {
        audioRef.current.volume = 1.0;
        audioRef.current.playbackRate = 1.0;
        audioRef.current.currentTime = 0;
      } catch (e) {
        console.warn('Error resetting audio properties:', e);
      }

      console.log('Starting audio playback');
      playingPromiseRef.current = audioRef.current.play()
        .then(() => {
          console.log('Audio playback started successfully');
          playingPromiseRef.current = null;
          if (mountedRef.current) {
            setIsPlaying(true);
          }
        })
        .catch(error => {
          console.error('Error playing audio:', error);
          playingPromiseRef.current = null;
          setAutoplayFailed(true);
          if (mountedRef.current) {
            setIsPlaying(false);
          }
        });
    }
  };

  const splitLessonText = useMemo(() => splitIntoSentences(lessonText), [lessonText]);
  const splitLessonTranslation = useMemo(() => splitIntoSentences(lessonTranslation), [lessonTranslation]);

  // Don't show anything during initial load
  if (isInitialLoad) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <p className="ml-3 text-gray-600">Loading lesson...</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <p className="ml-3 text-gray-600">Loading lesson content...</p>
          </div>
        </div>
      </div>
    );
  }

  console.log('Current state:', { lessonText, lessonTranslation, audioUrl, showUnknownWordsQuiz, unknownWords });

  if (showUnknownWordsQuiz && unknownWords.length > 0) {
    if (isAdmin) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full mx-auto p-6">
            <div className="bg-white rounded-lg shadow-md p-8 space-y-8">
              <h2 className="text-2xl font-bold text-center text-gray-900">Review Quiz</h2>
              
              <div className="flex flex-col items-center gap-6">
                <button
                  onClick={() => setShowUnknownWordsQuiz(false)}
                  className="flex items-center px-6 py-3 rounded-full bg-indigo-500 hover:bg-indigo-600 text-white transition-colors w-full justify-center"
                >
                  Skip Review Quiz
                </button>
                
                <button
                  onClick={() => {
                    setShowUnknownWordsQuiz(true);
                    setIsInitialLoad(false);
                  }}
                  className="flex items-center px-6 py-3 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors w-full justify-center"
                >
                  Take Review Quiz
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <WordTestPage 
        words={unknownWords}
        trackScore={true}
        onComplete={(score) => handleQuizComplete(score)}
      />
    );
  }

  // Show lesson text after quiz completion
  if (!showUnknownWordsQuiz) {
    return (
      <>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6 relative">
        <div className="max-w-md w-full mx-auto p-6">
          <div className="bg-white rounded-lg shadow-md p-8 space-y-8">
            <h2 className="text-2xl font-bold text-center text-gray-900">{t('Listen to the Lesson')}</h2>

            {showText && lessonText && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-4">
                {splitLessonText.map((sentence, index) => (
                  <div key={index}>
                    <p className="text-lg text-gray-800">{sentence}</p>
                    {lessonTranslation && splitLessonTranslation[index] && (
                      <p className="mt-1 text-gray-600 italic">
                        {splitLessonTranslation[index]}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col items-center gap-6">
              {autoplayFailed && !hasListenedToAudio && !isPlaying && (
                <p className="text-sm text-gray-600 text-center">
                  {t('Tap the play button to start audio. Your browser requires interaction.')}
                </p>
              )}

              <div className="w-full space-y-3">
                <button
                  onClick={playAudio}
                  className={`flex items-center px-6 py-3 rounded-lg transition-colors ${
                    isPlaying
                      ? 'bg-red-500 hover:bg-red-600'
                      : 'bg-indigo-500 hover:bg-indigo-600'
                  } text-white w-full justify-center shadow-md`}
                >
                  {isPlaying ? (
                    <>
                      <Pause className="h-5 w-5 mr-2" />
                      {t('Pause')}
                    </>
                  ) : (
                    <>
                      <Play className="h-5 w-5 mr-2" />
                      {t('Play')}
                    </>
                  )}
                </button>

                <div className="bg-gray-100 rounded-lg p-4 space-y-2">
                  <div className="relative w-full h-2 bg-gray-300 rounded-full overflow-hidden cursor-pointer"
                    onClick={(e) => {
                      if (audioRef.current && audioDuration > 0) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        const percentage = x / rect.width;
                        audioRef.current.currentTime = percentage * audioDuration;
                      }
                    }}
                  >
                    <div
                      className="absolute left-0 top-0 h-full bg-indigo-500 transition-all duration-100"
                      style={{ width: `${audioDuration > 0 ? (audioCurrentTime / audioDuration) * 100 : 0}%` }}
                    />
                  </div>

                  <div className="flex justify-between text-xs text-gray-600">
                    <span>{formatTime(audioCurrentTime)}</span>
                    <span>{formatTime(audioDuration)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowReportModal(true)}
            className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <Flag className="h-4 w-4" />
            {t('Report Issue')}
          </button>
        </div>
      </div>

      {showInitialSurvey && (
        <UnderstandingSurvey
          onSubmit={handleInitialSurveySubmit}
          nativeLanguage={nativeLanguage}
        />
      )}

      {showHighUnderstandingOptions && lessonId && (
        <HighUnderstandingOptions
          currentLessonId={lessonId}
          currentLevel={currentLevel}
          onPracticeAnyway={handlePracticeAnyway}
          onGoToNextLesson={handleGoToNextLesson}
          nativeLanguage={nativeLanguage}
        />
      )}

      {showReportModal && lessonId && userId && (
        <ReportIssueModal
          lessonId={lessonId}
          lessonTitle={lessonTitle}
          userId={userId}
          nativeLanguage={nativeLanguage}
          onClose={() => setShowReportModal(false)}
        />
      )}
    </>
    );
  }

  return null;
}